import {
  BACKEND_MAPPING_FIELDS,
  FIELD_VALUE_OPTIONS,
  MATERIAL_NO_FIELDS,
  VAS_LEVEL_OPTIONS,
} from '../../constants/filter-criteria.constants';
import { ConditionGroup } from '../../../models/filter-group.model';

export function isMaterialNoField(field: string): boolean {
  return MATERIAL_NO_FIELDS.includes(field?.toLowerCase());
}

export function normalizeSeparators(value: string): string {
  if (!value) {
    return value;
  }
  return value
    .replace(/[\r\n\t;]+/g, ',')
    .replace(/ {2,}/g, ',')
    .split(',')
    .map(part => part.trim())
    .filter(part => part !== '')
    .join(',');
}

export function deduplicateOptions(rawOptions: string[]): {
  displayOptions: string[];
  valueMap: Record<string, string[]>;
} {
  const groups: Record<string, string[]> = {};
  const firstOccurrence: Record<string, string> = {};

  for (const option of rawOptions) {
    const key = option.toLowerCase();
    if (!groups[key]) {
      groups[key] = [];
      firstOccurrence[key] = option;
    }
    groups[key].push(option);
  }

  const displayOptions = Object.keys(firstOccurrence).map(
    (key) => firstOccurrence[key],
  );

  const valueMap: Record<string, string[]> = {};
  for (const key of Object.keys(groups)) {
    valueMap[firstOccurrence[key]] = groups[key];
  }

  return { displayOptions, valueMap };
}

export function getValueOptions(field: string): string[] {
  return FIELD_VALUE_OPTIONS[field?.toLowerCase()] || [];
}

export function getSelectedVasTypes(groups: ConditionGroup[]): string[] {
  const vasTypes: string[] = [];

  for (const group of groups) {
    for (const cond of group.conditions) {
      if (cond.field !== 'VAS Type' || !cond.value) continue;

      if (Array.isArray(cond.value)) {
        vasTypes.push(...cond.value);
      } else if (typeof cond.value === 'string' && cond.value.trim()) {
        vasTypes.push(
          ...cond.value
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v),
        );
      }
    }
  }

  return [...new Set(vasTypes)];
}

export function getVasLevelOptions(selectedVasTypes: string[]): {
  level: string;
  name: string;
}[] {
  if (!selectedVasTypes.length) {
    return [];
  }

  return VAS_LEVEL_OPTIONS.filter(
    (o) => selectedVasTypes.includes(o.vasType) && o.level !== '',
  ).map((o) => ({ level: `${o.vasType}:${o.level}`, name: o.name }));
}

export function getRangeValues(includedValues: string): string[] {
  if (!includedValues) return [];

  return includedValues
    .split(',')
    .map((r: string) => r.trim())
    .filter((r: string) => r);
}

export function getExcludedValues(excludedValues: string): string[] {
  if (!excludedValues) return [];

  return excludedValues
    .split(',')
    .map((r: string) => r.trim())
    .filter((r: string) => r);
}

export function getAllValues(
  includedValues: string,
  excludedValues: string,
): string[] {
  const includedVals = getRangeValues(includedValues);
  const excludedVals = getExcludedValues(excludedValues);
  return [...new Set([...includedVals, ...excludedVals])];
}

export function mapBackendFieldToFrontend(
  backendField: string,
  fields: { label: string; value: string }[],
): string {
  if (BACKEND_MAPPING_FIELDS[backendField]) {
    return BACKEND_MAPPING_FIELDS[backendField];
  }

  const lowerBackendField = backendField.toLowerCase();
  for (const [key, value] of Object.entries(BACKEND_MAPPING_FIELDS)) {
    if (key.toLowerCase() === lowerBackendField) {
      return value;
    }
  }

  const field = fields.find(
    (f) =>
      f.value.toLowerCase() === backendField.toLowerCase() ||
      f.label.toLowerCase() === backendField.toLowerCase(),
  );

  return field ? field.label : backendField;
}

export function updateFilteredRanges(
  rangeSearchText: string,
  includedValues: string,
  excludedValues: string,
): string[] {
  const allValues = getAllValues(includedValues, excludedValues);

  if (!rangeSearchText.trim()) {
    return allValues;
  }

  const searchTerm = rangeSearchText.toLowerCase();
  return allValues.filter((value) =>
    value.toLowerCase().includes(searchTerm),
  );
}
